import { either } from 'fp-ts/es6/Either';
import { identity, tuple } from 'fp-ts/es6/function';
import { fromEither, fromNullable, isNone, none, option, some } from 'fp-ts/es6/Option';
import { pipe, pipeable } from 'fp-ts/es6/pipeable';
import { filter, isEmpty } from 'fp-ts/es6/Record';
import { failure, Int, string, success, Type } from 'io-ts';
import { stringify } from 'querystring';
import { parse as parseUrl } from 'url';
/**
 * @category routes
 * @since 0.4.0
 */
var Route = /** @class */ (function () {
    function Route(parts, query) {
        this.parts = parts;
        this.query = query;
    }
    /**
     * @since 0.4.0
     */
    Route.isEmpty = function (r) {
        return r.parts.length === 0 && isEmpty(r.query);
    };
    /**
     * @since 0.4.0
     */
    Route.parse = function (s, decode) {
        if (decode === void 0) { decode = true; }
        var route = parseUrl(s, true);
        var oparts = option.map(fromNullable(route.pathname), function (s) {
            var r = s.split('/').filter(Boolean);
            return decode ? r.map(decodeURIComponent) : r;
        });
        var parts = isNone(oparts) ? [] : oparts.value;
        return new Route(parts, Object.assign({}, route.query));
    };
    /**
     * @since 0.4.0
     */
    Route.prototype.toString = function (encode) {
        if (encode === void 0) { encode = true; }
        var nonUndefinedQuery = pipe(this.query, filter(function (value) { return value !== undefined; }));
        var qs = stringify(nonUndefinedQuery);
        var parts = encode ? this.parts.map(encodeURIComponent) : this.parts;
        return '/' + parts.join('/') + (qs ? '?' + qs : '');
    };
    /**
     * @since 0.4.0
     */
    Route.empty = new Route([], {});
    return Route;
}());
export { Route };
var assign = function (a) { return function (b) { return Object.assign({}, a, b); }; };
var PARSER_URI = 'fp-ts-routing/Parser';
/**
 * @category parsers
 * @since 0.4.0
 */
var Parser = /** @class */ (function () {
    function Parser(run) {
        this.run = run;
    }
    /**
     * @since 0.4.0
     */
    Parser.of = function (a) {
        return new Parser(function (s) { return some(tuple(a, s)); });
    };
    /**
     * @since 0.4.0
     */
    Parser.prototype.map = function (f) {
        return this.chain(function (a) { return Parser.of(f(a)); }); // <= derived
    };
    /**
     * @since 0.4.0
     */
    Parser.prototype.ap = function (fab) {
        var _this = this;
        return fab.chain(function (f) { return _this.map(f); }); // <= derived
    };
    /**
     * @since 0.4.0
     */
    Parser.prototype.chain = function (f) {
        var _this = this;
        return new Parser(function (r) { return option.chain(_this.run(r), function (_a) {
            var a = _a[0], r2 = _a[1];
            return f(a).run(r2);
        }); });
    };
    /**
     * @since 0.4.0
     */
    Parser.prototype.alt = function (that) {
        var _this = this;
        return new Parser(function (r) {
            var oar = _this.run(r);
            return isNone(oar) ? that.run(r) : oar;
        });
    };
    /**
     * @since 0.4.0
     */
    Parser.prototype.then = function (that) {
        return that.ap(this.map(assign));
    };
    return Parser;
}());
export { Parser };
/**
 * @category parsers
 * @since 0.4.0
 */
export function zero() {
    return new Parser(function () { return none; });
}
/**
 * @category parsers
 * @since 0.4.0
 */
export function parse(parser, r, a) {
    var oa = option.map(parser.run(r), function (_a) {
        var a = _a[0];
        return a;
    });
    return isNone(oa) ? a : oa.value;
}
/**
 * @category parsers
 * @since 0.5.1
 */
export var getParserMonoid = function () { return ({
    concat: function (x, y) { return x.alt(y); },
    empty: zero()
}); };
/**
 * @category parsers
 * @since 0.5.1
 */
export var parser = {
    URI: PARSER_URI,
    map: function (ma, f) { return ma.map(f); },
    of: Parser.of,
    ap: function (mab, ma) { return ma.ap(mab); },
    chain: function (ma, f) { return ma.chain(f); },
    alt: function (fx, f) {
        return new Parser(function (r) {
            var oar = fx.run(r);
            return isNone(oar) ? f().run(r) : oar;
        });
    },
    zero: zero
};
var _a = pipeable(parser), alt = _a.alt, ap = _a.ap, apFirst = _a.apFirst, apSecond = _a.apSecond, chain = _a.chain, chainFirst = _a.chainFirst, flatten = _a.flatten, map = _a.map;
export { 
/**
 * @category parsers
 * @since 0.5.1
 */
alt, 
/**
 * @category parsers
 * @since 0.5.1
 */
ap, 
/**
 * @category parsers
 * @since 0.5.1
 */
apFirst, 
/**
 * @category parsers
 * @since 0.5.1
 */
apSecond, 
/**
 * @category parsers
 * @since 0.5.1
 */
chain, 
/**
 * @category parsers
 * @since 0.5.1
 */
chainFirst, 
/**
 * @category parsers
 * @since 0.5.1
 */
flatten, 
/**
 * @category parsers
 * @since 0.5.1
 */
map };
/**
 * @category formatters
 * @since 0.4.0
 */
var Formatter = /** @class */ (function () {
    function Formatter(run) {
        this.run = run;
    }
    /**
     * @since 0.4.0
     */
    Formatter.prototype.contramap = function (f) {
        var _this = this;
        return new Formatter(function (r, b) { return _this.run(r, f(b)); });
    };
    /**
     * @since 0.4.0
     */
    Formatter.prototype.then = function (that) {
        var _this = this;
        return new Formatter(function (r, ab) { return that.run(_this.run(r, ab), ab); });
    };
    return Formatter;
}());
export { Formatter };
/**
 * @category formatters
 * @since 0.4.0
 */
export function format(formatter, a, encode) {
    if (encode === void 0) { encode = true; }
    return formatter.run(Route.empty, a).toString(encode);
}
var FORMATTER_URI = 'fp-ts-routing/Formatter';
/**
 * @category formatters
 * @since 0.5.1
 */
export var formatter = {
    URI: FORMATTER_URI,
    contramap: function (fa, f) { return fa.contramap(f); }
};
var contramap = pipeable(formatter).contramap;
export { 
/**
 * @category formatters
 * @since 0.5.1
 */
contramap };
/**
 * @category matchers
 * @since 0.4.0
 */
var Match = /** @class */ (function () {
    function Match(parser, formatter) {
        this.parser = parser;
        this.formatter = formatter;
    }
    /**
     * @since 0.4.0
     */
    Match.prototype.imap = function (f, g) {
        return new Match(this.parser.map(f), this.formatter.contramap(g));
    };
    /**
     * @since 0.4.0
     */
    Match.prototype.then = function (that) {
        var p = this.parser.then(that.parser);
        var f = this.formatter.then(that.formatter);
        return new Match(p, f);
    };
    return Match;
}());
export { Match };
/**
 * @category matchers
 * @since 0.5.1
 */
export function imap(f, g) {
    return function (ma) { return ma.imap(f, g); };
}
/**
 * @category matchers
 * @since 0.5.1
 */
export function then(mb) {
    return function (ma) { return ma.then(mb); };
}
var singleton = function (k, v) {
    var _a;
    return (_a = {}, _a[k] = v, _a);
};
/**
 * `succeed` matches everything but consumes nothing
 *
 * @category matchers
 * @since 0.4.0
 */
export function succeed(a) {
    return new Match(new Parser(function (r) { return some(tuple(a, r)); }), new Formatter(identity));
}
/**
 * `end` matches the end of a route
 *
 * @category matchers
 * @since 0.4.0
 */
export var end = new Match(new Parser(function (r) { return (Route.isEmpty(r) ? some(tuple({}, r)) : none); }), new Formatter(identity));
/**
 * `type` matches any io-ts type path component
 *
 * @example
 * import * as t from 'io-ts'
 * import { lit, type, Route } from 'fp-ts-routing'
 * import { some, none } from 'fp-ts/es6/Option'
 *
 * const T = t.keyof({
 *   a: null,
 *   b: null
 * })
 *
 * const match = lit('search').then(type('topic', T))
 *
 * assert.deepStrictEqual(match.parser.run(Route.parse('/search/a')), some([{ topic: 'a' }, Route.empty]))
 * assert.deepStrictEqual(match.parser.run(Route.parse('/search/b')), some([{ topic: 'b' }, Route.empty]))
 * assert.deepStrictEqual(match.parser.run(Route.parse('/search/')), none)
 *
 * @category matchers
 * @since 0.4.0
 */
export function type(k, type) {
    return new Match(new Parser(function (r) {
        if (r.parts.length === 0) {
            return none;
        }
        else {
            var head = r.parts[0];
            var tail_1 = r.parts.slice(1);
            return option.map(fromEither(type.decode(head)), function (a) { return tuple(singleton(k, a), new Route(tail_1, r.query)); });
        }
    }), new Formatter(function (r, o) { return new Route(r.parts.concat(type.encode(o[k])), r.query); }));
}
/**
 * `str` matches any string path component
 *
 * @example
 * import { str, Route } from 'fp-ts-routing'
 * import { some, none } from 'fp-ts/es6/Option'
 *
 * assert.deepStrictEqual(str('id').parser.run(Route.parse('/abc')), some([{ id: 'abc' }, new Route([], {})]))
 * assert.deepStrictEqual(str('id').parser.run(Route.parse('/')), none)
 *
 * @category matchers
 * @since 0.4.0
 */
export function str(k) {
    return type(k, string);
}
/**
 * @internal
 */
export var IntegerFromString = new Type('IntegerFromString', function (u) { return Int.is(u); }, function (u, c) {
    return either.chain(string.validate(u, c), function (s) {
        var n = +s;
        return isNaN(n) || !Number.isInteger(n) ? failure(s, c) : success(n);
    });
}, String);
/**
 * `int` matches any integer path component
 *
 * @example
 * import { int, Route } from 'fp-ts-routing'
 * import { some, none } from 'fp-ts/es6/Option'
 *
 * assert.deepStrictEqual(int('id').parser.run(Route.parse('/1')), some([{ id: 1 }, new Route([], {})]))
 * assert.deepStrictEqual(int('id').parser.run(Route.parse('/a')), none)
 *
 * @category matchers
 * @since 0.4.0
 */
export function int(k) {
    return type(k, IntegerFromString);
}
/**
 * `lit(x)` will match exactly the path component `x`
 *
 * @example
 * import { lit, Route } from 'fp-ts-routing'
 * import { some, none } from 'fp-ts/es6/Option'
 *
 * assert.deepStrictEqual(lit('subview').parser.run(Route.parse('/subview/')), some([{}, new Route([], {})]))
 * assert.deepStrictEqual(lit('subview').parser.run(Route.parse('/')), none)
 *
 * @category matchers
 * @since 0.4.0
 */
export function lit(literal) {
    return new Match(new Parser(function (r) {
        if (r.parts.length === 0) {
            return none;
        }
        else {
            var head = r.parts[0];
            var tail = r.parts.slice(1);
            return head === literal ? some(tuple({}, new Route(tail, r.query))) : none;
        }
    }), new Formatter(function (r) { return new Route(r.parts.concat(literal), r.query); }));
}
/**
 * Will match a querystring.
 *
 *
 * **Note**. Use `io-ts`'s `strict` instead of `type` otherwise excess properties won't be removed.
 *
 * @example
 * import * as t from 'io-ts'
 * import { lit, str, query, Route } from 'fp-ts-routing'
 *
 * const route = lit('accounts')
 *   .then(str('accountId'))
 *   .then(lit('files'))
 *   .then(query(t.strict({ pathparam: t.string })))
 *   .formatter.run(Route.empty, { accountId: 'testId', pathparam: '123' })
 *   .toString()
 * assert.strictEqual(route, '/accounts/testId/files?pathparam=123')
 *
 * @category matchers
 * @since 0.4.0
 */
export function query(type) {
    return new Match(new Parser(function (r) { return option.map(fromEither(type.decode(r.query)), function (query) { return tuple(query, new Route(r.parts, {})); }); }), new Formatter(function (r, query) { return new Route(r.parts, type.encode(query)); }));
}
